// src/command/commandRegistry.ts

import { Command, CommandCategory } from './types';
import { FEATURE_CAPABILITIES } from '../config/featureCapabilities';

// ============================================================
// Navigation Commands
// ============================================================

const navigationCommands: Command[] = [
  {
    id: 'nav-contacts',
    label: 'Go to Contacts',
    description: 'View and manage your contacts',
    keywords: ['contacts', 'people', 'clients', 'crm'],
    category: 'navigation',
    icon: 'users',
    action: (navigate) => navigate('/contacts'),
  },
  {
    id: 'nav-tasks',
    label: 'Go to Tasks',
    description: 'View and manage your tasks',
    keywords: ['tasks', 'todos', 'work', 'assignments'],
    category: 'navigation',
    icon: 'check-square',
    action: (navigate) => navigate('/tasks'),
  },
  {
    id: 'nav-pipeline',
    label: 'Go to Pipeline',
    description: 'View your sales pipeline',
    keywords: ['pipeline', 'deals', 'opportunities', 'sales'],
    category: 'navigation',
    icon: 'trending-up',
    action: (navigate) => navigate('/pipeline'),
  },
  // M1 — one canonical Messages destination.
  //
  // 'nav-messages' and 'nav-unibox' previously pointed at two different
  // pages (WeOpen live sessions and the Unibox) while both describing
  // themselves as messaging. They are now a single entry for the Contact
  // communications inbox, carrying the union of both keyword sets so the
  // old search terms — including "unibox" — still find it.
  {
    id: 'nav-messages',
    label: 'Go to Messages',
    description: 'Contact communications inbox — email, SMS and chat threads',
    keywords: [
      'messages', 'inbox', 'email', 'communication',
      'unibox', 'unified inbox', 'emails', 'sms', 'threads',
    ],
    category: 'navigation',
    icon: 'inbox',
    // messages.read — the palette is a navigation surface too, so it honours
    // the same capability as the sidebar. Without this a viewer denied the
    // menu could still jump to the page from the palette.
    capability: FEATURE_CAPABILITIES.viewMessages,
    action: (navigate) => navigate('/messages'),
  },
  // The two bounded contexts that used to be reachable only by impersonating
  // "Messages" now have their own honestly-named entries.
  {
    id: 'nav-assistant-conversations',
    label: 'Go to Assistant Conversations',
    description: 'AI assistant conversation sessions',
    keywords: ['assistant', 'ai', 'conversations', 'sessions', 'copilot'],
    category: 'navigation',
    icon: 'sparkles',
    action: (navigate) => navigate('/conversations'),
  },
  {
    id: 'nav-live-sessions',
    label: 'Go to Live Sessions',
    description: 'WeOpen live chat sessions',
    keywords: ['live', 'sessions', 'weopen', 'live chat'],
    category: 'navigation',
    icon: 'message-circle',
    action: (navigate) => navigate('/live-sessions'),
  },
  {
    id: 'nav-unibox-unread',
    label: 'Jump to Unread Messages',
    description: 'View only unread messages in Unibox',
    keywords: ['unread', 'new messages', 'inbox unread'],
    category: 'navigation',
    icon: 'mail',
    capability: FEATURE_CAPABILITIES.viewMessages,
    action: (navigate) => navigate('/messages?unreadOnly=true'),
  },
  {
    id: 'nav-unibox-calls',
    label: 'Jump to Calls',
    description: 'View call threads and voicemails',
    keywords: ['calls', 'voicemail', 'phone', 'missed calls'],
    category: 'navigation',
    icon: 'phone',
    capability: FEATURE_CAPABILITIES.viewMessages,
    action: (navigate) => navigate('/messages?medium=call'),
  },
  {
    id: 'nav-unibox-unread-calls',
    label: 'Jump to Unread Calls',
    description: 'View unread call threads and missed calls',
    keywords: ['unread calls', 'missed calls', 'new calls', 'voicemail unread'],
    category: 'navigation',
    icon: 'phone-missed',
    capability: FEATURE_CAPABILITIES.viewMessages,
    action: (navigate) => navigate('/messages?medium=call&unreadOnly=true'),
  },
  {
    id: 'nav-unibox-waiting',
    label: 'Jump to Waiting-On Threads',
    description: 'View threads marked as waiting for response',
    keywords: ['waiting', 'pending', 'follow up', 'awaiting response', 'waiting on'],
    category: 'navigation',
    icon: 'clock',
    capability: FEATURE_CAPABILITIES.viewMessages,
    action: (navigate) => navigate('/messages?status=waiting'),
  },
  {
    id: 'nav-channels',
    label: 'Go to Channels',
    description: 'View your AI channels',
    keywords: ['channels', 'ai', 'automation', 'bots'],
    category: 'navigation',
    icon: 'radio',
    action: (navigate) => navigate('/channels'),
  },
  {
    id: 'nav-marketplace',
    label: 'Go to Marketplace',
    description: 'Browse the marketplace',
    keywords: ['marketplace', 'store', 'browse', 'discover'],
    category: 'navigation',
    icon: 'shopping-bag',
    action: (navigate) => navigate('/marketplace'),
  },
  {
    id: 'nav-events',
    label: 'Go to Events',
    description: 'View your events and calendar',
    keywords: ['events', 'calendar', 'meetings', 'schedule'],
    category: 'navigation',
    icon: 'calendar',
    action: (navigate) => navigate('/schedule'),
  },
  {
    id: 'nav-projects',
    label: 'Go to Projects',
    description: 'View your projects',
    keywords: ['projects', 'work', 'management'],
    category: 'navigation',
    icon: 'folder',
    action: (navigate) => navigate('/projects'),
  },
  {
    id: 'nav-files',
    label: 'Go to Files',
    description: 'View your files and documents',
    keywords: ['files', 'documents', 'storage', 'uploads'],
    category: 'navigation',
    icon: 'file',
    action: (navigate) => navigate('/files'),
  },
  {
    id: 'nav-forms',
    label: 'Go to Forms',
    description: 'View and manage your forms',
    keywords: ['forms', 'surveys', 'questionnaires'],
    category: 'navigation',
    icon: 'clipboard',
    action: (navigate) => navigate('/forms'),
  },
  {
    id: 'nav-settings',
    label: 'Go to Settings',
    description: 'Manage your workspace settings',
    keywords: ['settings', 'preferences', 'configuration'],
    category: 'navigation',
    icon: 'settings',
    action: (navigate) => navigate('/settings'),
  },
  {
    id: 'nav-team',
    label: 'Go to Performance',
    description: 'View team performance, workload and clock-in status',
    // TEAM-UX-1 — the page was renamed Performance, but 'team' and
    // 'members' stay as keywords so existing muscle memory still finds
    // it. The command id and /team route are unchanged so deep links
    // and any stored shortcuts keep working.
    keywords: ['performance', 'team', 'members', 'workload', 'scorecard', 'staff'],
    capability: FEATURE_CAPABILITIES.viewTeam,
    category: 'navigation',
    icon: 'users',
    action: (navigate) => navigate('/team'),
  },
];

// ============================================================
// Quick Create Commands
// ============================================================

const createCommands: Command[] = [
  {
    id: 'create-contact',
    label: 'Create Contact',
    description: 'Add a new contact',
    keywords: ['new contact', 'add contact', 'create person'],
    capability: FEATURE_CAPABILITIES.createContact,
    category: 'create',
    icon: 'user-plus',
    action: (navigate) => navigate('/contacts'),
  },
  {
    id: 'create-task',
    label: 'Create Task',
    description: 'Add a new task',
    keywords: ['new task', 'add task', 'create todo'],
    capability: FEATURE_CAPABILITIES.createTask,
    category: 'create',
    icon: 'plus-square',
    action: (navigate) => navigate('/tasks'),
  },
  {
    id: 'create-event',
    label: 'Create Event',
    description: 'Schedule a new event',
    keywords: ['new event', 'add event', 'schedule meeting'],
    capability: { domain: 'events', action: 'write' },
    category: 'create',
    icon: 'calendar-plus',
    action: (navigate) => navigate('/schedule?new=1'),
  },
  {
    id: 'create-opportunity',
    label: 'Create Deal',
    description: 'Add a new deal to your pipeline',
    keywords: ['new deal', 'add deal', 'new opportunity', 'create lead'],
    capability: FEATURE_CAPABILITIES.createDeal,
    category: 'create',
    icon: 'target',
    action: (navigate) => navigate('/pipeline'),
  },
  {
    id: 'create-form',
    label: 'Create Form',
    description: 'Create a new form',
    keywords: ['new form', 'add form', 'create survey'],
    capability: { domain: 'forms', action: 'write' },
    category: 'create',
    icon: 'file-text',
    action: (navigate) => navigate('/forms'),
  },
  {
    id: 'create-project',
    label: 'Create Project',
    description: 'Start a new project',
    keywords: ['new project', 'add project', 'create project'],
    capability: { domain: 'projects', action: 'write' },
    category: 'create',
    icon: 'folder-plus',
    action: (navigate) => navigate('/projects'),
  },
  {
    id: 'create-channel',
    label: 'Create Channel',
    description: 'Create a new AI channel',
    keywords: ['new channel', 'add channel', 'create bot'],
    capability: { domain: 'channels', action: 'write' },
    category: 'create',
    icon: 'radio',
    action: (navigate) => navigate('/channels'),
  },
];

// ============================================================
// System Commands
// ============================================================

const systemCommands: Command[] = [
  {
    id: 'switch-workspace',
    label: 'Switch Workspace',
    description: 'Change to a different workspace',
    keywords: ['switch workspace', 'change workspace', 'workspaces'],
    category: 'system',
    icon: 'repeat',
    action: (navigate) => navigate('/workspaces'),
  },
  {
    id: 'open-billing',
    label: 'Open Billing',
    description: 'View billing and subscription',
    keywords: ['billing', 'subscription', 'payment', 'invoice'],
    capability: FEATURE_CAPABILITIES.viewBilling,
    category: 'system',
    icon: 'credit-card',
    action: (navigate) => navigate('/settings?tab=billing-credits'),
  },
  {
    id: 'open-audit-logs',
    label: 'Open Audit Logs',
    description: 'View workspace activity logs',
    keywords: ['audit', 'logs', 'activity', 'history'],
    capability: FEATURE_CAPABILITIES.viewAuditLog,
    category: 'system',
    icon: 'activity',
    action: (navigate) => navigate('/audit'),
  },
  {
    id: 'open-integrations',
    label: 'Open Integrations',
    description: 'Manage connected apps and integrations',
    keywords: ['integrations', 'apps', 'connections', 'api'],
    capability: FEATURE_CAPABILITIES.viewIntegrations,
    category: 'system',
    icon: 'link',
    action: (navigate) => navigate('/integrations'),
  },
  {
    id: 'open-profile',
    label: 'Edit Profile',
    description: 'Update your profile settings',
    keywords: ['profile', 'account', 'user settings'],
    category: 'system',
    icon: 'user',
    action: (navigate) => navigate('/settings?tab=profile'),
  },
  {
    id: 'open-system-health',
    label: 'Open System Health',
    description: 'View system errors and performance monitoring',
    keywords: ['system health', 'errors', 'monitoring', 'performance', 'debug'],
    capability: FEATURE_CAPABILITIES.viewSystemHealth,
    category: 'system',
    icon: 'activity',
    action: (navigate) => navigate('/system/health'),
  },
  {
    id: 'open-launch-check',
    label: 'Open Launch Check',
    description: 'Check production readiness and configuration',
    keywords: ['launch', 'production', 'readiness', 'config', 'go-no-go', 'deploy', 'diagnostics'],
    category: 'system',
    icon: 'rocket',
    action: (navigate) => navigate('/system/launch-check'),
  },
];

// ============================================================
// AI Commands
// ============================================================

const aiCommands: Command[] = [
  {
    id: 'ai-ask',
    label: 'Ask LevelUp',
    description: 'Start a conversation with LevelUp AI',
    keywords: ['ask', 'ai', 'assistant', 'help', 'question'],
    capability: FEATURE_CAPABILITIES.useAI,
    category: 'ai',
    icon: 'message-circle',
    // There is no standalone /assistant route; assistant sessions live on
    // /conversations.
    action: (navigate) => navigate('/conversations'),
  },
  {
    id: 'ai-channels',
    label: 'Open AI Channels',
    description: 'Browse AI-powered channels',
    keywords: ['ai channels', 'automation', 'bots'],
    category: 'ai',
    icon: 'cpu',
    action: (navigate) => navigate('/channels?type=ai'),
  },
  // 'ai-create-tasks' and 'ai-follow-ups' were removed: they targeted
  // /assistant?action=…, a route that never existed, so they always landed
  // on Not Found. Re-add them when a real destination exists.
];

// ============================================================
// All Commands
// ============================================================

export const allCommands: Command[] = [
  ...navigationCommands,
  ...createCommands,
  ...systemCommands,
  ...aiCommands,
];

// ============================================================
// Category Labels
// ============================================================

export const categoryLabels: Record<CommandCategory, string> = {
  navigation: 'Navigation',
  create: 'Quick Create',
  system: 'System',
  ai: 'AI',
};

// ============================================================
// Get Commands by Category
// ============================================================

export function getCommandsByCategory(category: CommandCategory): Command[] {
  return allCommands.filter((cmd) => cmd.category === category);
}

// ============================================================
// Filter Commands by Capability
// ============================================================

export function filterCommandsByCapability(
  commands: Command[],
  hasCapability: (domain: string, action: string) => boolean
): Command[] {
  return commands.filter((cmd) => {
    if (!cmd.capability) return true;
    return hasCapability(cmd.capability.domain, cmd.capability.action);
  });
}

/** @deprecated Use filterCommandsByCapability */
export function filterCommandsByPermission(
  commands: Command[],
  hasPermission: (permission: string) => boolean
): Command[] {
  return commands.filter((cmd) => {
    if (!cmd.permission) return true;
    return hasPermission(cmd.permission);
  });
}

// ============================================================
// Fuzzy Search Commands
// ============================================================

export function searchCommands(commands: Command[], query: string): Command[] {
  if (!query.trim()) return commands;

  const lowerQuery = query.toLowerCase();

  return commands
    .map((cmd) => {
      let score = 0;

      // Exact label match
      if (cmd.label.toLowerCase() === lowerQuery) {
        score += 100;
      }
      // Label starts with query
      else if (cmd.label.toLowerCase().startsWith(lowerQuery)) {
        score += 80;
      }
      // Label contains query
      else if (cmd.label.toLowerCase().includes(lowerQuery)) {
        score += 60;
      }

      // Description contains query
      if (cmd.description?.toLowerCase().includes(lowerQuery)) {
        score += 30;
      }

      // Keywords match
      if (cmd.keywords?.some((kw) => kw.toLowerCase().includes(lowerQuery))) {
        score += 40;
      }

      return { cmd, score };
    })
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score)
    .map(({ cmd }) => cmd);
}
